<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useLanguageStore } from "@/stores/languageStore.js";
import { useCustomStarMapStore } from "@/stores/customStarMapStore.js";
import { generateStarNodes, optimizeImageFile } from "@/utils/starMapGenerator.js";
import { STAR_MAP_TEMPLATE_CATEGORIES, getTemplatesByCategory } from "@/data/starMapTemplates.js";
import OriginalImageViewer from "@/components/OriginalImageViewer.vue";
import StarMapWorkshopNav from "@/components/StarMapWorkshopNav.vue";
import { localizedCreatorText } from "@/utils/localizedCreatorText.js";

const route = useRoute();
const router = useRouter();
const languageStore = useLanguageStore();
const starMapStore = useCustomStarMapStore();

const fileInput = ref(null);
const editorStage = ref(null);
const imageDataUrl = ref("");
const imageStorageKey = ref("");
const pendingImageBlob = ref(null);
const imageWidth = ref(1);
const imageHeight = ref(1);
const aspectRatio = ref(1);
const mapName = ref("");
const mapDescription = ref("");
const visibility = ref("private");
const nodeCount = ref(5);
const nodes = ref([]);
const busy = ref(false);
const draggingIndex = ref(-1);
const statusText = ref("");
const errorText = ref("");
function localizedSaveError(error) {
  if (Number(error?.status) >= 500 || /internal server error/i.test(String(error?.message || ""))) {
    return languageStore.t("starMapSaveError");
  }
  if (error?.code === "PUBLISH_LIMIT") return languageStore.t("publishLimitReached");
  if (error?.code === "WORKSHOP_BANNED") return languageStore.t("workshopBanned");
  return error?.message || languageStore.t("starMapSaveError");
}
const imageChanged = ref(false);
const trajectoryEditing = ref(false);
const originalOpen = ref(false);
const nodeEditorOpen = ref(false);
const templatePickerOpen = ref(false);
const templateCategory = ref("world");
const currentTemplateId = ref("");
const editingMapId = ref("");
const originalNodeCount = ref(0);
let nodeCountSyncTimer = 0;

const nodeCountMin = starMapStore.MIN_NODE_COUNT;
const nodeCountMax = starMapStore.MAX_NODE_COUNT;
const nodeCountIsValid = computed(() => {
  const value = Number(nodeCount.value);
  return nodeCount.value !== ""
    && Number.isInteger(value)
    && value >= nodeCountMin
    && value <= nodeCountMax;
});
const nodeCountError = computed(() => nodeCountIsValid.value
  ? ""
  : `${languageStore.t("levelCountPoints")}：${nodeCountMin}–${nodeCountMax}`
);
const canSave = computed(() => Boolean(
  imageDataUrl.value
  && nodeCountIsValid.value
  && nodes.value.length === Number(nodeCount.value)
  && !busy.value
));
const stageStyle = computed(() => ({ aspectRatio: String(aspectRatio.value || 1) }));
const visibleTemplates = computed(() => getTemplatesByCategory(templateCategory.value));

function readImageMeta(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve({
      width: Math.max(1, image.naturalWidth || image.width || 1),
      height: Math.max(1, image.naturalHeight || image.height || 1)
    });
    image.onerror = () => reject(new Error(languageStore.t("cannotReadTemplate")));
    image.src = src;
  });
}

function hydrateFromMap(map) {
  if (!map) return;
  editingMapId.value = map.id;
  imageDataUrl.value = map.imageDataUrl;
  imageStorageKey.value = map.imageStorageKey || "";
  pendingImageBlob.value = null;
  imageWidth.value = map.imageWidth;
  imageHeight.value = map.imageHeight;
  aspectRatio.value = map.aspectRatio;
  mapName.value = localizedCreatorText(languageStore, map.name, "name");
  mapDescription.value = localizedCreatorText(languageStore, map.description, "description");
  visibility.value = map.visibility || "private";
  nodeCount.value = map.nodes.length;
  originalNodeCount.value = map.nodes.length;
  nodes.value = map.nodes.map(node => ({ ...node }));
  imageChanged.value = false;
}

function stableNode(node, level) {
  return {
    id: String(node?.id || `node-${level}`),
    level,
    x: Math.max(0.025, Math.min(0.975, Number(node?.x) || 0.5)),
    y: Math.max(0.025, Math.min(0.975, Number(node?.y) || 0.5)),
    color: /^#[0-9a-f]{6}$/i.test(String(node?.color || ""))
      ? String(node.color)
      : "#7ff8ef",
    ...(node?.levelData ? { levelData: node.levelData } : {})
  };
}

async function reconcileNodeCount(count) {
  const targetCount = Math.max(
    nodeCountMin,
    Math.min(nodeCountMax, Number(count) || 5)
  );
  nodeCount.value = targetCount;

  if (!editingMapId.value || nodes.value.length === 0) {
    nodes.value = (await generateStarNodes(imageDataUrl.value, targetCount))
      .map((node, index) => stableNode(node, index + 1));
    return;
  }

  // Existing maps are incremental: retained nodes never move, re-number or lose IDs.
  if (targetCount <= nodes.value.length) {
    nodes.value = nodes.value
      .slice(0, targetCount)
      .map((node, index) => stableNode(node, index + 1));
    return;
  }

  const generated = await generateStarNodes(imageDataUrl.value, targetCount);
  const retained = nodes.value.map((node, index) => stableNode(node, index + 1));
  const appended = generated
    .slice(retained.length, targetCount)
    .map((node, index) => stableNode(node, retained.length + index + 1));

  nodes.value = [...retained, ...appended];
}

watch(nodeCount, nextCount => {
  window.clearTimeout(nodeCountSyncTimer);
  if (!nodeCountIsValid.value) return;
  if (!imageDataUrl.value) return;
  nodeCountSyncTimer = window.setTimeout(async () => {
    if (busy.value) return;
    try {
      await reconcileNodeCount(nextCount);
      trajectoryEditing.value = false;
      errorText.value = "";
      statusText.value = languageStore.t("regenerateNodesSuccess", {
        count: nodes.value.length
      });
    } catch (error) {
      errorText.value = error?.message || languageStore.t("starMapImageError");
    }
  }, 120);
});

async function generateNodes() {
  if (busy.value) return;
  if (!imageDataUrl.value) {
    errorText.value = languageStore.t("starMapUploadEmptyTitle");
    statusText.value = "";
    return;
  }
  errorText.value = "";
  trajectoryEditing.value = !trajectoryEditing.value;
  statusText.value = trajectoryEditing.value
    ? languageStore.t("dragNodesHint", { count: nodes.value.length })
    : "";
}

async function handleFile(event) {
  const file = event.target.files?.[0];
  if (!file) return;
  busy.value = true;
  errorText.value = "";
  statusText.value = languageStore.t("starMapProcessing");
  try {
    const optimized = await optimizeImageFile(file);
    imageDataUrl.value = optimized.dataUrl;
    imageStorageKey.value = "";
    pendingImageBlob.value = optimized.blob;
    currentTemplateId.value = "";
    imageChanged.value = true;
    imageWidth.value = optimized.width;
    imageHeight.value = optimized.height;
    aspectRatio.value = optimized.aspectRatio;
    if (!mapName.value) mapName.value = file.name.replace(/\.[^.]+$/, "").slice(0, 32);

    // For an existing map, changing the background must not regenerate retained nodes.
    await reconcileNodeCount(nodeCount.value);
    trajectoryEditing.value = false;
    statusText.value = languageStore.t("regenerateNodesSuccess", {
      count: nodes.value.length
    });
  } catch (error) {
    errorText.value = error?.message || languageStore.t("starMapImageError");
    statusText.value = "";
  } finally {
    busy.value = false;
    if (event.target) event.target.value = "";
  }
}

function removeSelectedImage() {
  if (busy.value) return;
  imageDataUrl.value = "";
  imageStorageKey.value = "";
  pendingImageBlob.value = null;
  imageWidth.value = 1;
  imageHeight.value = 1;
  aspectRatio.value = 1;
  nodes.value = [];
  originalNodeCount.value = 0;
  currentTemplateId.value = "";
  imageChanged.value = true;
  nodeEditorOpen.value = false;
  trajectoryEditing.value = false;
  originalOpen.value = false;
  statusText.value = "";
  errorText.value = "";
}

function openTemplatePicker(category) {
  templateCategory.value = category;
  templatePickerOpen.value = true;
}

async function chooseTemplate(template) {
  if (!template || busy.value) return;
  busy.value = true;
  errorText.value = "";
  statusText.value = languageStore.t("loadingTemplate", {
    name: template.nameKey ? languageStore.t(template.nameKey) : template.name
  });
  try {
    const meta = await readImageMeta(template.image);
    imageDataUrl.value = template.image;
    imageStorageKey.value = "";
    pendingImageBlob.value = null;
    currentTemplateId.value = template.id;
    imageWidth.value = meta.width;
    imageHeight.value = meta.height;
    aspectRatio.value = meta.width / meta.height;
    imageChanged.value = true;
    if (!mapName.value.trim()) {
      mapName.value = template.nameKey
        ? languageStore.t(template.nameKey)
        : template.name;
    }
    if (!mapDescription.value.trim()) {
      mapDescription.value = template.descriptionKey
        ? languageStore.t(template.descriptionKey)
        : template.description;
    }
    templatePickerOpen.value = false;

    // Trusted templates are a background change only for an existing map.
    await reconcileNodeCount(nodeCount.value);
    trajectoryEditing.value = false;
    statusText.value = languageStore.t("regenerateNodesSuccess", {
      count: nodes.value.length
    });
  } catch (error) {
    errorText.value = error?.message || languageStore.t("templateLoadFailed");
  } finally {
    busy.value = false;
  }
}

function goParent() {
  if (route.query.from === "map-manager") {
    router.push({ name: "map-manager" });
    return;
  }
  if (route.query.from === "level-map-selector") {
    router.push({ name: "level-map-selector" });
    return;
  }
  if (route.query.from === "custom-map-view" && route.query.parentMapId) {
    router.push({ name: "custom-map-view", params: { mapId: String(route.query.parentMapId) } });
    return;
  }
  router.push({ name: "workshop" });
}

function mapNodesPayload() {
  return nodes.value.map((node, index) => {
    const level = index + 1;
    return {
      id: String(node?.id || `node-${level}`),
      level,
      x: Number(node.x),
      y: Number(node.y),
      color: String(node.color || "#7ff8ef")
    };
  });
}

async function resolveRemoteImageReference() {
  if (!starMapStore.backendEnabled) {
    return {
      imageUrl: imageDataUrl.value,
      imageStorageKey: ""
    };
  }

  if (pendingImageBlob.value) {
    const uploaded = await starMapStore.uploadRemoteImage(pendingImageBlob.value);
    imageDataUrl.value = String(uploaded?.imageUrl || "");
    imageStorageKey.value = String(uploaded?.storageKey || "");
    pendingImageBlob.value = null;
    if (!imageDataUrl.value) {
      throw new Error(languageStore.t("starMapSaveError"));
    }
  }

  return {
    imageUrl: imageDataUrl.value,
    imageStorageKey: imageStorageKey.value
  };
}

function commonMapPayload() {
  return {
    name: mapName.value.trim() || languageStore.t("starMapDefaultName"),
    description: mapDescription.value.trim().slice(0, 180),
    visibility: visibility.value,
    nodeCount: nodes.value.length,
    nodes: mapNodesPayload()
  };
}

async function persistCurrentMap({ continueToLevels = false } = {}) {
  if (!canSave.value) return null;
  busy.value = true;
  errorText.value = "";
  try {
    let saved;

    if (starMapStore.backendEnabled) {
      const payload = commonMapPayload();

      if (!editingMapId.value || imageChanged.value) {
        const image = await resolveRemoteImageReference();
        payload.imageUrl = image.imageUrl;
        payload.imageWidth = imageWidth.value;
        payload.imageHeight = imageHeight.value;
        payload.aspectRatio = aspectRatio.value;
        if (image.imageStorageKey) {
          payload.imageStorageKey = image.imageStorageKey;
        }
      }

      saved = editingMapId.value
        ? await starMapStore.updateRemoteMap(editingMapId.value, payload)
        : await starMapStore.createRemoteMap(payload);
    } else {
      // Local mode remains available during D2, but destructive reset flags are gone.
      saved = starMapStore.saveMap({
        ...commonMapPayload(),
        imageDataUrl: imageDataUrl.value,
        imageWidth: imageWidth.value,
        imageHeight: imageHeight.value,
        aspectRatio: aspectRatio.value
      }, {
        mapId: editingMapId.value || null
      });
    }

    if (!saved) throw new Error(languageStore.t("starMapSaveError"));

    hydrateFromMap(saved);
    originalNodeCount.value = saved.nodes.length;

    if (continueToLevels) {
      statusText.value = languageStore.t("starMapSaved");
      await router.push({
        name: "custom-level-editor",
        params: { mapId: saved.id, level: 1 },
        query: { from: "star-map-editor" }
      });
    } else {
      statusText.value = languageStore.t("mapUpdatedSaved");
    }
    return saved;
  } catch (error) {
    errorText.value = localizedSaveError(error);
    return null;
  } finally {
    busy.value = false;
  }
}

async function saveCurrentMap() {
  await persistCurrentMap({ continueToLevels: true });
}

async function saveMapOnly() {
  await persistCurrentMap({ continueToLevels: false });
}

function nodeIsLit(node) {
  return Boolean(
    editingMapId.value
    && starMapStore.isLit(node.level, editingMapId.value)
  );
}

function beginDrag(event, index) {
  if (busy.value) return;
  draggingIndex.value = index;
  event.preventDefault();
  updateDraggedNode(event);
}

function updateDraggedNode(event) {
  const index = draggingIndex.value;
  const stage = editorStage.value;
  if (index < 0 || !stage || !nodes.value[index]) return;
  const rect = stage.getBoundingClientRect();
  if (!rect.width || !rect.height) return;
  const x = Math.max(0.025, Math.min(0.975, (event.clientX - rect.left) / rect.width));
  const y = Math.max(0.025, Math.min(0.975, (event.clientY - rect.top) / rect.height));
  nodes.value[index] = { ...nodes.value[index], x, y };
}

function stopDrag() {
  draggingIndex.value = -1;
}

onMounted(async () => {
  window.addEventListener("pointermove", updateDraggedNode, { passive: false });
  window.addEventListener("pointerup", stopDrag);
  window.addEventListener("pointercancel", stopDrag);

  const requestedId = route.query.mapId ? String(route.query.mapId) : "";
  if (!requestedId) {
    starMapStore.refresh();
    return;
  }

  try {
    const requested = starMapStore.backendEnabled
      ? await starMapStore.loadRemoteMap(requestedId)
      : (starMapStore.refresh(), starMapStore.getMap(requestedId));
    if (requested) hydrateFromMap(requested);
  } catch (error) {
    errorText.value = localizedSaveError(error);
  }
});

onBeforeUnmount(() => {
  window.clearTimeout(nodeCountSyncTimer);
  window.removeEventListener("pointermove", updateDraggedNode);
  window.removeEventListener("pointerup", stopDrag);
  window.removeEventListener("pointercancel", stopDrag);
});
</script>

<template>
  <section class="star-editor page-card">
    <div class="page-header editor-header titleless-header">
      <button class="back-button" type="button" @click="goParent">← {{ languageStore.t('back') }}</button>
      <button class="header-link" type="button" :disabled="!imageDataUrl" @click="originalOpen = true">{{ languageStore.t('viewOriginal') }}</button>
    </div>

    <StarMapWorkshopNav />

    <div class="editor-toolbar">
      <label class="field wide">
        <span>{{ languageStore.t('mapName') }}</span>
        <input v-model="mapName" maxlength="48" :placeholder="languageStore.t('starMapDefaultName')" />
      </label>

      <label class="field wide description-field">
        <span>{{ languageStore.t('mapDescription') }}</span>
        <textarea
          v-model="mapDescription"
          maxlength="180"
          rows="2"
          :placeholder="languageStore.t('mapDescriptionPlaceholder')"
        />
      </label>

      <label class="field count-field">
        <span>{{ languageStore.t('levelCountPoints') }}</span>
        <input
          v-model.number="nodeCount"
          type="number"
          :min="nodeCountMin"
          :max="nodeCountMax"
          :class="{ invalid: nodeCountError }"
          :aria-invalid="nodeCountError ? 'true' : 'false'"
        />
        <small v-if="nodeCountError" class="field-error">{{ nodeCountError }}</small>
      </label>
      <label class="field">
        <span>{{ languageStore.t('mapPermission') }}</span>
        <select v-model="visibility">
          <option value="private">{{ languageStore.t('privateMap') }}</option>
          <option value="share">{{ languageStore.t('shareMap') }}</option>
          <option value="public">{{ languageStore.t('publicMap') }}</option>
        </select>
      </label>
      <input ref="fileInput" class="sr-only" type="file" accept="image/*" @change="handleFile" />
    </div>


    <div v-if="imageDataUrl" class="map-preview-wrap">
      <div
        ref="editorStage"
        class="map-preview-card"
        :class="{ editing: trajectoryEditing }"
      >
        <img :src="imageDataUrl" alt="" class="preview-image" draggable="false" />
        <button
          v-for="(node, index) in nodes"
          :key="`preview-${node.id || node.level}`"
          class="preview-node"
          :class="{ lit: nodeIsLit(node) }"
          type="button"
          :style="{ left: `${node.x * 100}%`, top: `${node.y * 100}%`, '--node-color': node.color || '#7ff8ef' }"
          :aria-label="`${languageStore.t('level')} ${node.level}`"
          @pointerdown="trajectoryEditing && beginDrag($event, index)"
        ><span /></button>
        <span class="preview-hint"></span>
      </div>
      <button
        class="preview-remove"
        type="button"
        :aria-label="languageStore.t('cancel')"
        @click.stop="removeSelectedImage"
      >×</button>
    </div>

    <div v-else class="upload-empty" role="button" :aria-label="languageStore.t('selectTemplateOrUpload')" @click="fileInput?.click()">
      <span class="upload-empty-icon" aria-hidden="true">＋</span>
      <span class="upload-empty-text">{{ languageStore.t('selectTemplateOrUpload') }}</span>
    </div>

    <div class="main-editor-actions">
      <button
        class="regenerate-button neon-button secondary"
        type="button"
        :disabled="busy"
        @click="generateNodes"
      >
        {{ languageStore.t('regenerateByCount') }}
      </button>
      <button
        class="quick-save-button neon-button"
        type="button"
        :disabled="!canSave || busy"
        @click="saveCurrentMap"
      >
        {{ languageStore.t('save') }}
      </button>
    </div>

    <div class="editor-footer">
      <p v-if="statusText" class="status-text">{{ statusText }}</p>
      <p v-if="errorText" class="error-text">{{ errorText }}</p>
    </div>

    <div v-if="templatePickerOpen" class="template-modal" role="dialog" aria-modal="true">
      <div class="template-shell">
        <div class="template-topbar">
          <div>
            <strong>{{ languageStore.t('selectMapTemplate') }}</strong>
            <span>{{ languageStore.t('selectTemplateDescription', { count: nodeCount }) }}</span>
          </div>
          <button class="modal-close" type="button" :aria-label="languageStore.t('close')" @click="templatePickerOpen = false">×</button>
        </div>

        <div class="template-tabs">
          <button
            v-for="category in STAR_MAP_TEMPLATE_CATEGORIES"
            :key="`tab-${category.id}`"
            type="button"
            :class="{ active: templateCategory === category.id }"
            @click="templateCategory = category.id"
          >
            {{ languageStore.t(category.labelKey) }}
          </button>
        </div>

        <div class="template-grid">
          <button
            v-for="template in visibleTemplates"
            :key="template.id"
            type="button"
            class="template-card"
            :class="{ selected: currentTemplateId === template.id }"
            @click="chooseTemplate(template)"
          >
            <img :src="template.image" alt="" />
            <span class="template-copy">
              <strong>{{
  template.nameKey
    ? languageStore.t(template.nameKey)
    : template.name
}}</strong>
              <small>{{
  template.descriptionKey
    ? languageStore.t(template.descriptionKey)
    : template.description
}}</small>
            </span>
          </button>
        </div>
      </div>
    </div>


    <OriginalImageViewer :open="originalOpen" :src="imageDataUrl" :title="`${mapName || languageStore.t('myStarMap')} - ${languageStore.t('originalImageTitle')}`" @close="originalOpen = false" />
  </section>
</template>

<style scoped>
.star-editor {
  min-height: calc(100dvh - 64px);
  padding: 18px 22px 18px;
  display: flex;
  flex-direction: column;
}
.titleless-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:10px; }
.header-link { border:0; background:transparent; color:#91fff6; font-size:13px; font-weight:800; padding:5px 0; }
.header-link:disabled { opacity:.35; }
.editor-toolbar { display:grid; grid-template-columns:minmax(220px,1.6fr) 140px 180px; gap:9px; align-items:end; margin-top:10px; }
.field { display:grid; gap:6px; color:rgba(255,255,255,.72); font-size:12px; font-weight:700; }
.field.wide { grid-column:1/-1; }
.field input,.field select,.field textarea { width:100%; border:1px solid rgba(82,246,232,.26); border-radius:11px; color:#fff; background:rgba(5,28,44,.88); padding:0 10px; outline:none; font:inherit; }
.field input,.field select { min-height:42px; }
.field textarea { min-height:62px; padding:9px 10px; resize:none; line-height:1.35; }
.field input:focus,.field select:focus,.field textarea:focus { border-color:rgba(82,246,232,.75); }
.field input.invalid { border-color:#ff7385; box-shadow:0 0 0 1px rgba(255,115,133,.18); }
.field-error { color:#ff98a6; font-size:11px; font-weight:700; line-height:1.25; }
.template-source-panel { display:grid; gap:6px; margin-top:10px; }
.template-label { color:rgba(255,255,255,.72); font-size:12px; font-weight:800; }
.template-source-row { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:7px; }
.source-button { min-width:0; min-height:42px; display:flex; align-items:center; justify-content:center; gap:6px; border:1px solid rgba(82,246,232,.25); border-radius:11px; color:#eafffd; background:rgba(4,31,48,.82); }
.source-button span { color:#7ff7ed; font-size:17px; line-height:1; }
.source-button strong { overflow:hidden; font-size:12px; white-space:nowrap; text-overflow:ellipsis; }
.source-button:hover { border-color:rgba(82,246,232,.68); background:rgba(12,66,78,.62); }
.source-button.custom { border-style:dashed; }
.map-preview-wrap,.upload-empty { position:relative; width:100%; flex:1 1 260px; min-height:230px; margin-top:10px; }
.map-preview-wrap { overflow:hidden; border:1px dashed rgba(82,246,232,.34); border-radius:16px; background:rgba(2,14,24,.34); }
.map-preview-card { position:absolute; inset:0; display:block; width:100%; height:100%; padding:0; overflow:hidden; border:0; background:transparent; cursor:pointer; }
.upload-empty { overflow:hidden; border:1px dashed rgba(82,246,232,.34); border-radius:16px; background:rgba(2,14,24,.34); cursor:pointer; }
.preview-remove { position:absolute; z-index:6; top:12px; right:12px; display:grid; place-items:center; width:40px; height:40px; padding:0; border:1px solid rgba(135,255,246,.62); border-radius:50%; color:#dffffc; background:rgba(2,20,32,.9); box-shadow:0 4px 18px rgba(0,0,0,.35); font-size:28px; line-height:1; cursor:pointer; }
.preview-remove:hover { border-color:#8ffff6; background:rgba(12,65,76,.96); }
.preview-image { position:absolute; inset:0; width:100%; height:100%; object-fit:contain; background:#020b13; }
.preview-node { position:absolute; z-index:3; display:grid; place-items:center; width:28px; height:28px; padding:0; transform:translate(-50%,-50%); border:0; background:transparent; pointer-events:none; touch-action:none; }
.preview-node > span { width:12px; height:12px; border-radius:50%; background:var(--node-color); border:2px solid #fff; box-shadow:0 0 0 3px rgba(127,248,239,.2),0 0 10px var(--node-color),0 0 18px rgba(127,248,239,.78); }
.preview-node.lit > span { width:14px; height:14px; box-shadow:0 0 0 4px rgba(255,255,255,.2),0 0 12px var(--node-color),0 0 24px var(--node-color); }
.map-preview-card.editing .preview-node { pointer-events:auto; cursor:grab; }
.map-preview-card.editing .preview-node > span { width:17px; height:17px; border-color:#fff; box-shadow:0 0 0 6px rgba(127,248,239,.22),0 0 14px var(--node-color),0 0 26px var(--node-color); }
.map-preview-card.editing .preview-node:active { cursor:grabbing; }
.preview-hint { position:absolute; left:50%; bottom:10px; transform:translateX(-50%); padding:5px 10px; border-radius:999px; color:#cffffa; background:rgba(1,14,24,.74); border:1px solid rgba(116,255,244,.26); font-size:12px; font-weight:800; white-space:nowrap; }
.upload-empty { display:grid; place-items:center; align-content:center; gap:8px; }
.upload-empty-icon { display:grid; place-items:center; width:56px; height:56px; border:1px solid rgba(82,246,232,.45); border-radius:50%; color:#8ffdf3; font-size:34px; font-weight:300; }
.upload-empty-text { color:rgba(255,255,255,.66); font-size:13px; font-weight:800; }
.main-editor-actions { display:grid; grid-template-columns:minmax(0,1.7fr) minmax(110px,.75fr); gap:9px; margin-top:9px; }
.regenerate-button,.quick-save-button { width:100%; min-height:42px; margin:0; }
.editor-footer { margin-top:5px; min-height:18px; }
.status-text { color:#99fff6; font-size:12px; margin:3px 0; }
.error-text { color:#ff9aa7; font-size:12px; margin:3px 0; }
.template-modal,.node-editor-modal { position:fixed; z-index:1400; inset:0; display:grid; place-items:center; padding:18px; background:rgba(0,5,12,.88); backdrop-filter:blur(8px); }
.template-shell,.node-editor-shell { width:min(980px,96vw); max-height:94dvh; display:flex; flex-direction:column; gap:12px; padding:14px; overflow:auto; border:1px solid rgba(92,255,241,.42); border-radius:20px; background:#041625; box-shadow:0 24px 80px rgba(0,0,0,.5); }
.template-topbar,.node-editor-topbar { display:flex; align-items:center; justify-content:space-between; gap:14px; }
.template-topbar > div,.node-editor-topbar > div { display:grid; gap:2px; min-width:0; }
.template-topbar strong,.node-editor-topbar strong { color:#fff; font-size:18px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.template-topbar span,.node-editor-topbar span { color:rgba(255,255,255,.58); font-size:12px; }
.modal-close { width:38px; height:38px; flex:0 0 38px; border:1px solid rgba(108,255,244,.34); border-radius:50%; background:rgba(4,30,45,.92); color:#b9fff9; font-size:27px; line-height:1; }
.template-tabs { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:7px; }
.template-tabs button { min-height:38px; border:1px solid rgba(82,246,232,.19); border-radius:10px; color:rgba(255,255,255,.65); background:rgba(4,28,44,.72); font-weight:800; }
.template-tabs button.active { color:#fff; border-color:rgba(82,246,232,.7); background:rgba(15,76,89,.78); }
.template-grid { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:10px; overflow:auto; }
.template-card { min-width:0; padding:0; overflow:hidden; border:1px solid rgba(82,246,232,.2); border-radius:14px; color:#fff; background:rgba(2,19,32,.9); text-align:left; }
.template-card.selected { border-color:#7ff8ef; box-shadow:0 0 0 1px rgba(127,248,239,.25); }
.template-card img { width:100%; aspect-ratio:3/2; display:block; object-fit:cover; background:#020b13; }
.template-copy { display:grid; gap:3px; padding:9px 10px 11px; }
.template-copy strong { font-size:13px; }
.template-copy small { display:-webkit-box; overflow:hidden; color:rgba(255,255,255,.52); font-size:10px; line-height:1.35; -webkit-line-clamp:2; -webkit-box-orient:vertical; }
.map-editor-stage { position:relative; width:100%; margin:0 auto; overflow:hidden; border:1px solid rgba(82,246,232,.32); border-radius:16px; background:#030a14; user-select:none; touch-action:none; }
.modal-stage { max-height:72dvh; min-height:340px; }
.editor-image { position:absolute; inset:0; width:100%; height:100%; object-fit:fill; pointer-events:none; }
.editor-node { position:absolute; z-index:3; width:34px; height:34px; border:0; transform:translate(-50%,-50%); color:#fff; background:transparent; display:grid; place-items:center; cursor:grab; touch-action:none; }
.editor-node span { width:10px; height:10px; border-radius:50%; background:rgba(8,18,28,.82); border:1px solid rgba(255,255,255,.95); }
.editor-node strong { position:absolute; left:21px; top:18px; font-size:9px; color:#fff; text-shadow:0 1px 5px #000,0 0 3px #000; }
.editor-node.lit span { width:24px; height:24px; border:0; background:transparent; border-radius:0; position:relative; }
.editor-node.lit span::before { content:'✦'; position:absolute; inset:0; display:grid; place-items:center; color:var(--node-color); font-size:22px; line-height:1; text-shadow:0 0 5px #fff,0 0 10px var(--node-color),0 0 18px var(--node-color); animation:editorStarTwinkle 1.35s ease-in-out infinite; }
.editor-node.dragging { transform:translate(-50%,-50%) scale(1.18); cursor:grabbing; }
@keyframes editorStarTwinkle { 0%,100%{transform:scale(.82) rotate(-4deg);opacity:.74} 50%{transform:scale(1.18) rotate(4deg);opacity:1} }
.node-editor-actions { display:grid; grid-template-columns:1fr; gap:9px; }
.node-editor-actions .neon-button { min-height:44px; }
.sr-only { position:absolute; width:1px; height:1px; overflow:hidden; clip:rect(0,0,0,0); white-space:nowrap; }

@media (max-width:850px) {
  .editor-toolbar { grid-template-columns:1fr 1fr; }
  .field.wide { grid-column:1/-1; }
  .template-grid { grid-template-columns:repeat(2,minmax(0,1fr)); }
}

@media (max-width:560px) {
  .star-editor {
    width:100%;
    min-height:calc(100dvh - 28px);
    padding:8px 9px 9px;
    border-radius:18px;
  }
  .titleless-header { margin-bottom:5px; }
  .titleless-header .back-button { font-size:16px; padding:4px 0; }
  .header-link { font-size:15px; }
  .editor-toolbar { grid-template-columns:1fr 1fr; gap:6px; margin-top:5px; }
  .field { gap:4px; font-size:15px; line-height:1.08; }
  .field input,.field select { min-height:36px; padding:0 9px; font-size:15px; font-weight:700; }
  .field textarea { min-height:48px; padding:7px 9px; font-size:15px; font-weight:650; line-height:1.22; }
  .template-source-panel { gap:4px; margin-top:7px; }
  .template-label { font-size:15px; }
  .template-source-row { display:flex; gap:6px; overflow-x:auto; scrollbar-width:none; padding-bottom:1px; }
  .template-source-row::-webkit-scrollbar { display:none; }
  .source-button { flex:1 0 76px; min-height:36px; padding:4px 7px; gap:4px; }
  .source-button span { font-size:14px; }
  .source-button strong { font-size:14px; }
  .map-preview-wrap,.upload-empty {
    flex:1 1 auto;
    min-height:220px;
    height:auto;
    margin-top:7px;
    border-radius:14px;
  }
  .upload-empty-icon { width:54px; height:54px; font-size:33px; }
  .upload-empty-text,.preview-hint { font-size:14px; }
  .main-editor-actions { grid-template-columns:minmax(0,1.55fr) minmax(92px,.75fr); gap:7px; margin-top:7px; }
  .regenerate-button,.quick-save-button { min-height:40px; padding:7px 9px; font-size:15px; }
  .editor-footer { min-height:0; margin-top:3px; }
  .status-text,.error-text { font-size:11px; margin:2px 0; }
  .template-modal,.node-editor-modal { padding:5px; align-items:center; }
  .template-shell,.node-editor-shell { width:100%; max-height:98dvh; padding:9px; gap:8px; border-radius:16px; }
  .template-topbar strong,.node-editor-topbar strong { font-size:17px; }
  .template-topbar span,.node-editor-topbar span { font-size:12px; }
  .modal-close { width:34px; height:34px; flex-basis:34px; font-size:24px; }
  .template-tabs { gap:5px; }
  .template-tabs button { min-height:36px; font-size:14px; }
  .template-grid { grid-template-columns:repeat(2,minmax(0,1fr)); gap:7px; }
  .template-copy { padding:7px 8px 9px; }
  .template-copy strong { font-size:14px; }
  .template-copy small { font-size:11px; }
  .modal-stage { min-height:0; max-height:none; height:min(68dvh,680px); }
  .editor-node { width:31px; height:31px; }
  .editor-node strong { left:19px; top:16px; font-size:9px; }
  .node-editor-actions .neon-button { min-height:42px; padding:7px 8px; font-size:14px; line-height:1.15; white-space:normal; }
}

@media (max-width:380px) {
  .star-editor { padding-inline:7px; }
  .field,.field input,.field select,.field textarea { font-size:14px; }
  .template-label { font-size:14px; }
  .source-button strong { font-size:13px; }
  .map-preview-wrap,.upload-empty { min-height:200px; }
  .template-grid { grid-template-columns:1fr 1fr; }
  .modal-stage { height:64dvh; }
  .node-editor-actions .neon-button { font-size:13px; }
  .main-editor-actions { grid-template-columns:minmax(0,1.45fr) minmax(82px,.7fr); gap:6px; }
  .regenerate-button,.quick-save-button { font-size:13px; }
}

/* REMOVE_RED_FRAME_TEXT_V1_START */
.template-section,
.template-tabs,
.template-options,
.map-template,
.map-template-row,
.map-template-tabs,
.map-template-options,
.template-picker,
.template-picker-row {
  display: none !important;
}
/* REMOVE_RED_FRAME_TEXT_V1_END */


/* REMOVE_EDITOR_RED_FRAME_REMAINS_V2_START */
.template-section,
.template-tabs,
.template-options,
.template-picker,
.template-picker-row,
.map-template,
.map-template-row,
.map-template-tabs,
.map-template-options,
.generated-hint,
.generated-status,
.generation-hint,
.generation-status,
.form-hint,
.helper-text,
.description-label,
.map-description-label,
.map-description-text {
  display: none !important;
}
/* REMOVE_EDITOR_RED_FRAME_REMAINS_V2_END */



</style>
